import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";

// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Authentication layout components
import CoverLayout from "pages/authentication/components/CoverLayout";

// Images
import bgImage from "assets/images/bg-reset-cover-2.png";
import checkMark from "assets/images/check-mark.png";

import { clearErrors } from "store/actions/userActions";
import setAlert from "store/actions/alertActions";
import { useHandleError } from "components/helper";
import { markAsReadFn } from "store/actions/orderAction";
import Loader from "components/Loader";

const ConfirmRequest = () => {
  const dispatch = useDispatch();
  const { token } = useParams();
  const error = useSelector((state) => state.users.error);
  const user = useSelector((state) => state.users.user);
  const isAuthenticated = useSelector((state) => state.users.isAuthenticated);
  const orderError = useSelector((state) => state.order.error);
  const isConfirming = useSelector((state) => state.order.isConfirming); // Get the loading state

  // Dispatch markAsReadFn on refresh if token and itemId exist
  useEffect(() => {
    if (token && isAuthenticated) {
      dispatch(markAsReadFn(token));
    }
  }, [token, isAuthenticated, dispatch]);

  useHandleError(error, dispatch, setAlert, clearErrors); // Use the custom hook

  if (!token || !isAuthenticated) {
    return <Navigate to={`/authentication/login?page=confirm-request/${token}`} />;
  }

  return (
    <CoverLayout coverHeight="50vh" image={bgImage}>
      <Card>
        <MDBox pb={6} px={3}>
          {(() => {
            if (isConfirming) {
              return (
                <MDBox
                  mt={3}
                  sx={{ display: "flex", flexDirection: "column" }}
                  textAlign="center"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Loader size={40} />
                </MDBox>
              );
            }

            if (orderError && orderError?.length > 0) {
              return (
                <MDBox
                  mt={3}
                  sx={{ display: "flex", flexDirection: "column" }}
                  textAlign="center"
                  justifyContent="center"
                  alignItems="center"
                >
                  <MDTypography variant="h3" fontWeight="small" color="error" mt={1}>
                    {orderError === "Order already confirmed" ? orderError : "Something went wrong"}
                  </MDTypography>
                </MDBox>
              );
            }

            return (
              <MDBox
                mt={3}
                sx={{ display: "flex", flexDirection: "column" }}
                textAlign="center"
                justifyContent="center"
                alignItems="center"
              >
                <MDBox
                  width={40}
                  minHeight={40}
                  py={6}
                  sx={{
                    backgroundImage: `url(${checkMark})`,
                    backgroundSize: "auto",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                />
                <MDTypography variant="h3" fontWeight="medium" color="primary" mt={1}>
                  Confirmation Sent
                </MDTypography>
                <MDTypography display="block" variant="button" color="primary" my={1} p={1}>
                  The guest will recieve your confirmation shortly.
                </MDTypography>
              </MDBox>
            );
          })()}

          <MDBox
            mt={3}
            sx={{ display: "flex", flexDirection: "column" }}
            textAlign="center"
            justifyContent="center"
            alignItems="center"
          >
            <MDTypography variant="button" color="text">
              <MDTypography
                component={Link}
                to={`/${user?.role || "admin"}/dashboards`}
                variant="button"
                color="primary"
                fontWeight="medium"
                textGradient
              >
                Go to the dashboard
              </MDTypography>
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    </CoverLayout>
  );
};

export default ConfirmRequest;
